<template>
  <Drawer :direction="isDesktop ? 'right' : 'bottom'" :open="open" @update:open="handleOpenChange">
    <DrawerContent
      class="z-[999] flex flex-col bg-white font-Spectral md:h-screen [&_.bg-muted]:hidden"
      :class="isDesktop ? '' : '!h-[100dvh] max-h-[100dvh] overflow-hidden'"
      :position="isDesktop ? 'right' : 'bottom'"
    >
      <!-- SP: 上部固定ヘッダー（タイトル + 閉じる） -->
      <div v-if="!isDesktop" class="flex shrink-0 items-center justify-between border-b border-gray-200 bg-white px-4 py-3">
        <DrawerTitle class="text-lg font-normal text-gray-600">{{ ITINERARY_REQUEST_FORM_TITLE }}</DrawerTitle>
        <button
          aria-label="ドロワーを閉じる"
          class="flex h-10 w-10 items-center justify-center rounded p-2 text-gray-500 hover:bg-gray-100"
          data-testid="close-itinerary-contact-drawer"
          type="button"
          @click="closeDrawer"
        >
          <img alt="" class="h-5 w-5" src="@/assets/icon/close/black.svg" />
        </button>
      </div>
      <img
        v-if="isDesktop"
        alt="close modal"
        class="absolute right-4 top-4 z-[3] h-10 w-10 cursor-pointer p-2"
        data-testid="close-itinerary-contact-drawer"
        src="@/assets/icon/close/black.svg"
        @click="closeDrawer"
      />
      <DrawerDescription class="min-h-0 flex-1 overflow-y-auto px-4 pb-4 pt-2 [-webkit-overflow-scrolling:touch] md:px-8 md:pb-8 md:pt-14">
        <p v-if="isDesktop" class="mb-4 text-center text-2xl text-gold3 md:text-3xl">{{ ITINERARY_REQUEST_FORM_TITLE }}</p>
        <ItineraryContactForm
          ref="formRef"
          :background-wide-media-url="backgroundWideMediaUrl"
          :itinerary-tag-label="itineraryTagLabel"
          :itinerary-title="itineraryTitle"
          :micro-c-m-s-content-id="microCMSContentId"
          :on-submit-success="onSubmitSuccess"
          :page-url="pageUrl"
          :price-text="priceText"
        />
      </DrawerDescription>
      <!-- 下部固定フッター（Submit） -->
      <footer class="shrink-0 border-t border-gray-200 bg-white px-4 py-4 pb-[max(1rem,env(safe-area-inset-bottom))]">
        <AtomsPrimaryButton aria-label="submit" class="w-full" :disabled="isSubmitting" :on-click="handleSubmitClick">
          <span>{{ isSubmitting ? 'sending...' : 'Submit' }}</span>
        </AtomsPrimaryButton>
      </footer>
    </DrawerContent>
  </Drawer>
</template>

<script lang="ts" setup>
import { useMediaQuery } from '@vueuse/core';

import AtomsPrimaryButton from '@/components/atoms/PrimaryButton/PrimaryButton.vue';
import { Drawer, DrawerContent, DrawerDescription, DrawerTitle } from '@/components/lib/shadcn-vue/drawer';
import { ITINERARY_REQUEST_FORM_TITLE } from '@/wabunka-src/features/Itinerary/constants/itineraryContact';
import ItineraryContactForm from '@/wabunka-src/features/Itinerary/pages/Itinerary/components/ItineraryContactForm/ItineraryContactForm.vue';

const isDesktop = useMediaQuery('(min-width: 768px)');

interface Props {
  /** 開閉状態（親が true にすると開く） */
  open: boolean;
  /** ドロワーを閉じるときに親が呼ぶコールバック（閉じるボタン・オーバーレイクリック時） */
  onClose: () => void;
  onSubmitSuccess: () => void;
  backgroundWideMediaUrl: string;
  itineraryTagLabel: string;
  itineraryTitle: string;
  microCMSContentId: string;
  pageUrl: string;
  priceText?: string;
}

const props = defineProps<Props>();

/** 子の Drawer が閉じようとしたときに親に伝える */
const handleOpenChange = (value: boolean) => {
  if (value === false) {
    props.onClose();
  }
};

const closeDrawer = () => {
  props.onClose();
};

const formRef = ref<InstanceType<typeof ItineraryContactForm> | null>(null);

const isSubmitting = ref(false);

const handleSubmitClick = async () => {
  if (isSubmitting.value) return;
  isSubmitting.value = true;
  try {
    await formRef.value?.submit();
  } finally {
    isSubmitting.value = false;
  }
};
</script>
